import { useContext } from "react";
import type { ReactNode } from "react";
import { StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { KeyboardStickyView } from "./sheetKeyboard";
import { PayStatusButton } from "./native/PayStatusButton";
import { KEYBOARD_GAP, sheetFoot } from "./native/sheetButton";
import type { SheetButtonState } from "./native/sheetButton";
import { SheetGrow, SheetHold } from "./native/sheetHold";

/** The sheet's content: clear of the safe area, its height kept while the
 *  checkout folds away (`hold`), and the one button at the bottom. Kept to
 *  a sheet's room (`keyboard="content"`), it shrinks what can (a vendor's
 *  scrolling form), never the button. Every purchase draws its content
 *  in one (PurchaseSheet). */
export function TicketsSheetFrame({
  testID,
  hold,
  button,
  selecting,
  after,
  sticky = false,
  children,
}: {
  testID: string;
  hold: boolean;
  button: SheetButtonState | null;
  /** The tickets are up: the button keeps their gap above it. */
  selecting: boolean;
  /** Under the button: what follows a status (a vendor's outcome). */
  after?: ReactNode;
  /** The button rides the keyboard, KEYBOARD_GAP above it (a sheet that
   *  keeps its height while a form is typed into: `keyboard="content"`). */
  sticky?: boolean;
  children: ReactNode;
}) {
  const insets = useSafeAreaInsets();
  const foot = sheetFoot(insets.bottom);
  const grow = useContext(SheetGrow);
  return (
    <View
      style={[styles.container, grow && styles.grow, { paddingBottom: foot }]}
      testID={testID}
    >
      <SheetHold hold={hold}>
        {children}
        {button ? (
          <KeyboardStickyView
            enabled={sticky}
            // Up by the keyboard's height, less the room under the button:
            // it lands KEYBOARD_GAP above the keyboard.
            offset={{ closed: 0, opened: foot - KEYBOARD_GAP }}
            style={selecting ? styles.selectButton : null}
          >
            <PayStatusButton {...button} />
          </KeyboardStickyView>
        ) : null}
        {after}
      </SheetHold>
    </View>
  );
}

const styles = StyleSheet.create({
  // Right under the handle's room, as the tickets sheet on Link in Bio
  // starts its screens under its handle.
  container: { flexShrink: 1, paddingHorizontal: 16 },
  grow: { flexGrow: 1 },
  // The tickets' own gap above their button (the shared ticket screen's).
  selectButton: { marginTop: 16 },
});
